<template>
  <template
    v-if="$store.state.challenges.detail.errorId !== Number(this.$route.params.id)"
  >
    <div class="challenge-detail" v-if="challenge && !isLoadingChallenge" ref="scrollable">
      <ChallengeHero :challenge="challenge"/>

      <Container class="challenge-content-container">
        <div class="challenge-content-left-side">
          <div class="challenge-logo-sdg-container" v-if="!challenge.isAnonymous">
            <div class="challenge-organisation-logo" v-if="challenge.actor" @click="goToActorDetail">
              <ActorAvatar
                class="challenge-organisation-image"
                variant="rounded"
                :actor-logo-url="challenge.actor.url"
                :actor="challenge.actor"
              />

              <div
                v-if="challenge?.actor?.description"
                class="challenge-organisation-description"
                v-html="challenge?.actor?.description?.slice(0, 100)"
              />
            </div>
            <actor-detail-SDGs
              target="simplified-challenge"
              :class="!challenge.actor ? 'sdg-no-actors' : ''"
              :selected-goals="sdgs"
            />
          </div>

          <div class="challenge-info" v-if="challenge.contact && !challenge.isAnonymous">
            <div class="challenge-contact">
              <AvatarNew
                :user="challenge.contact"
                :show-details="true"
              />
            </div>
            <div class="challenge-contact-button" v-if="!challenge.isAnonymous">
              <DsButton
                :disabled="!canStartConversation(challenge.contact)"
                style="width: 100%;"
                variant="rounded"
                :label="$t('get_in_touch')"
                @click="startConversation(challenge.contact.id)"
              />
            </div>
          </div>

          <div class="challenge-info">
            <h3 class="challenge-info-title">{{ challengeLabel }} info</h3>

            <div class="challenge-bottom-container">

              <ChallengeCountdown :challenge="challenge"/>

              <div class="challenge-enddate" v-if="challengeEndDate">
                <Icon name="clock"/>
                <p style="margin-left: 0.5rem; font-size: 14px;"><strong>{{ challengeEndDate }}</strong></p>
              </div>
              <div class="challenge-goals" v-if="challenge && challengeGoals.length && challengeGoals.length > 0">
                <Icon name="hand-shake"/>
                <p
                  style="margin-left: 0.5rem; font-size: 14px;"
                  v-tooltip.top="challengeGoals.length > 3 ? challengeGoals.join(', ') : ''"
                >
                  <strong>{{
                      challengeGoals.length > 3 ? challengeGoals.slice(0, 3).join(', ') + '...' : challengeGoals.slice(0, 3).join(', ')
                    }}</strong></p>
              </div>
              <div
                class="challenge-goals"
                v-if="challenge.geographicalLabels && challenge.geographicalLabels.length > 0"
              >
                <Icon name="map-marker"/>
                <p style="margin-left: 0.5rem; font-size: 14px;"><strong>{{
                    geographicalLabels.join(', ')
                  }}</strong></p>
              </div>

              <div class="challenge-actions">
                <DsButton
                  variant="rounded"
                  style="width: 100%;"
                  :label="$t('actor_edit')"
                  icon="edit"
                  @click="showEditChallengePanel"
                  v-if="challenge.id && canUserEditChallenge"
                />
                <DsButton
                  style="margin-top: 0.5rem"
                  v-if="isLoggedIn && canUserAccessChallengeDetail"
                  :variant="challenge.isFollowing ? 'rounded-secondary' : 'rounded'"
                  :label="challenge.isFollowing ? $t('intelligence_simplified_following') : $t('intelligence_simplified_follow')"
                  :loading="togglingPinStatus"
                  @click.stop="togglePinStatus"
                />
              </div>
            </div>

          </div>
        </div>

        <div class="challenge-content-right-side">
          <div class="profile-tabs-container mt-4">
            <div class="with-profile-tabs--tabs has-3-tabs">
              <router-link
                to="#overview" class="profile-tab"
                title=""
                :class="{'selected': $route.hash === '#overview' || !$route.hash}"
                @click="subNavSection = ''"
              >
                {{ $t('challenges_navigation_overview') }}
              </router-link>
              <router-link
                to="#qanda" class="profile-tab"
                title="Q&A"
                :class="{'selected': $route.hash === '#qanda'}"
                @click="subNavSection = 'q&a'"
              >
                {{ $t('challenges_navigation_qanda') }}
              </router-link>
              <router-link
                to="#updates"
                class="profile-tab"
                title="Updates"
                :class="{'selected': $route.hash === '#updates'}"
                @click="subNavSection = 'updates'"
              >
                {{ $t('challenges_navigation_updates') }}
              </router-link>
              <router-link
                to="#documents" class="profile-tab"
                title="Documents"
                :class="{'selected': $route.hash === '#documents'}"
                @click="subNavSection = 'documents'"
              >
                {{ $t('challenges_navigation_documents') }}
              </router-link>
              <router-link
                v-if="canDisplayMatchesTab"
                to="#match"
                class="profile-tab"
                title="Match"
                :class="{'selected': $route.hash === '#match'}"
                @click="subNavSection = 'match'"
              >
                {{ $t('challenges_navigation_match') }}
              </router-link>
            </div>
          </div>


          <div v-if="subNavSection === ''" class="challenge-content">
            <div class="challenge-content-section challenge-description" v-if="challenge.description">
              <p v-html="challenge.description"></p>
            </div>

            <div class="challenge-content-section" v-if="challenge.background && challenge.background.length > 8">
              <h2>{{ $t('challenge_background_of_the_challenge', { challenge: challengeLabel }) }}</h2>
              <p v-html="challenge.background"></p>
            </div>

            <div class="challenge-content-section" v-if="challenge.objective && challenge.objective.length > 8">
              <h2>{{ $t('challenges_what_are_we_looking_for') }}</h2>
              <p v-html="challenge.objective"></p>
            </div>
          </div>
          <div v-else-if="subNavSection === 'q&a'" class="challenge-content">
            <Comments
              :comments="comments"
              :comments-limit="100"
              :resource-id="String(challenge.id)"
              :show-title="false"
              :only-show-add-comment-input="false"
              @add-comment="addComment"
              @delete-comment="fetchComments"
            />
          </div>
          <div v-else-if="subNavSection === 'updates'" class="challenge-content"> <!-- Activity log -->
            <div class="activity-container">
              <div class="activity-list">
                <LoadingNew v-if="isLoadingUpdates"/>
                <div
                  v-for="update in updates" :key="update.id" class="activity-card"
                  v-else-if="updates.length > 0"
                >
                  <div class="activity-content">
                    <div class="activity-user">
                      <AvatarNew :user="update.user" :show-details="true"/>
                      <div class="update-actions">
                        {{ fromNow(update.createdAt) }}
                        <DsButton
                          v-if="update.user.id === userId"
                          class="button--minimal button--default"
                          icon="trash"
                          size="small"
                          iconSize="18"
                          @click="deleteChallengeUpdate(update)"
                        />
                      </div>
                    </div>
                    <div style="margin-top: 0.5rem;">
                      <p v-html="update.content"></p>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div
                    style="background-color: var(--primary-community-extra-lightest); width: 100%; padding: 2rem; border-radius: 0.5rem"
                  >
                    <h4>No updates yet</h4>
                  </div>
                </div>
              </div>
              <div v-if="canUserEditChallenge">
                <Comments
                  :only-show-add-comment-input="true"
                  @add-comment="addUpdate"
                />
              </div>
            </div>
          </div>
          <div v-else-if="subNavSection === 'documents'" class="challenge-content">
            <div class="documents-container">
              <div class="documents-actions">
                <DsButton
                  v-if="canDisplayDocumentsTab"
                  variant="rounded" size="small" label="Add document" icon="plus"
                  @click="openCreateResourcePanel"
                />
              </div>
              <div class="documents-list">
                <LoadingNew v-if="isLoadingDocuments"/>
                <div
                  v-for="document in documents" :key="document.id" class="document-card"
                  @click="openResource(document)" v-else-if="documents.length > 0"
                >
                  <div class="document-content">
                    <span><strong>{{ document.title }}</strong></span>
                    <p style="word-break: break-word; -webkit-line-clamp: 2; text-overflow: ellipsis; overflow: hidden; display: -webkit-box; -webkit-box-orient: vertical;">
                      {{
                        document.description
                      }}</p>
                  </div>
                  <div class="document-actions">
                    <DsButton
                      variant="rounded-secondary"
                      size="extra-small"
                      style="max-height: 30px; background-color: var(--primary-community-extra-lightest); color: var(--primary-community)"
                      :label="getFileType(document)"
                      :disabled="true"
                    />
                    <DsButton
                      v-if="isMember || challenge.creator.id === userId"
                      variant="rounded"
                      icon="edit"
                      size="small"
                      style="max-height: 30px;"
                      @click.stop="showEditResourcePanel(document)"
                    />

                    <DsButton
                      v-if="isMember || challenge.creator.id === userId"
                      variant="rounded"
                      icon="trash"
                      size="small"
                      style="max-height: 30px;"
                      @click.stop="showDeleteResourceConfirmationModal(document)"
                    />
                  </div>
                </div>
                <div v-else>
                  <div
                    style="background-color: var(--primary-community-extra-lightest); width: 100%; padding: 2rem; border-radius: 0.5rem"
                  >
                    <h4>No documents yet</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ChallengeMatchTab
            v-else-if="subNavSection === 'match'"
            :challenge-id="challengeId"
            :challenge="challenge"
          />
        </div>
      </Container>

      <div
        class="challenge-detail__more-challenges"
        v-if="similarAndNextChallenges.length > 0"
      >
        <Container class="container__added-padding">
          <h3>{{ $t('more_actor_profiles') }}</h3>
          <div class="row" style="margin-top: 1rem;">
            <div
              class="col-xs-12 col-sm-12 col-md-4 col-lg-4 col-xl-4"
              v-for="similarChallenge in similarAndNextChallenges.slice(0, 3)"
            >
              <ChallengeCardNew :challenge="similarChallenge"/>
            </div>
          </div>
        </Container>
      </div>
    </div>
  </template>
  <template v-else-if="isLoadingChallenge">
    <LoadingNew/>
  </template>
  <template
    v-else-if="$store.state.challenges.detail.errorStatusCode && $store.state.challenges.detail.errorStatusCode == 404"
  >
    <div style="text-align: center; margin: auto">
      <h3 class="h3">This {{ challengeLabel }} has been removed.</h3>
      <a href="/challenges">View all {{ challengesLabel }}</a>
    </div>
  </template>
  <template v-else>
    <div style="text-align: center; margin: auto">
      <h3 class="h3">You are not allowed to view the contents of this
        {{ challengeLabel }}</h3>
      <a href="#" @click="showLoginModal()" v-if="!isLoggedIn">Log in</a>
      <a href="/profile/subscription#billing" v-else-if="canShowSubscriptions">Upgrade</a>
    </div>
  </template>
</template>

<script lang="ts">
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import { defineComponent } from 'vue'
  import UserMixin from '@/util/UserMixin'
  import ChallengeMixin from '@/util/ChallengeMixin'
  import ChallengeHero from '@/components/Challenges/ChallengeHero.vue'
  import { getClearbitActorLogo } from '../../util/actor'
  import Container from '@/components/Container/Container.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { fromNow, getChallengeTimeDifferenceValue } from '../../util/date'
  import ActorDetailSDGs from '@/pages/ActorDetail/ActorDetailSDGs.vue'
  import BasicHero from '@/components/BasicHero/BasicHero.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import _uniqBy from 'lodash/uniqBy'
  import {
    addChallengeUpdate,
    createComment,
    fetchChallengeUpdates,
    fetchSimilarChallenges,
    getComments,
    removeChallengeUpdate,
    updatePinStatus
  } from '@/Domain/Challenge/Api/challenges'
  import ChallengeCountdown from '@/components/Challenges/ChallengeCountdown.vue'
  import KnowledgeBaseSimplified from '@/components/Dashboard/KnowledgeBaseSimplified.vue'
  import Comments from '@/components/Comments.vue'
  import moment from 'moment'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { fetchKnowledgeBaseData } from '@/api/knowledge-base'
  import MODAL_IDS from '@/constants/modal-ids'
  import { deleteEcosystemFile } from '@/api/files'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'
  import Tabs from '@/components/Tabs/Tabs.vue'
  import Tag from '@/components/Tag/Tag.vue'
  import Avatar from '@/components/Avatar/Avatar.vue'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import UserActivityMixin from '@/Domain/User/Mixins/UserActivityMixin'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import ActorBlock from '@/components/Dashboard/EmailReports/ActorBlock.vue'
  import ActorSuggestionCard from '@/components/SpottingAreas/ActorSuggestionCard.vue'
  import { ConversationsMixin } from '@/util/ConversationsMixin'
  import ChallengeMatchTab from '@/components/Challenges/ChallengeMatchTab.vue'

  export default defineComponent({
    name: 'challenge-detail-new',
    mixins: [TranslationsMixin, UserMixin, ChallengeMixin, UserActivityMixin, ConversationsMixin],
    components: {
      ChallengeMatchTab,
      ActorSuggestionCard,
      ActorBlock,
      ActorAvatar,
      AvatarNew,
      Avatar,
      Tag,
      Tabs,
      LoadingNew,
      Comments,
      KnowledgeBaseSimplified,
      ChallengeCountdown,
      ChallengeCardNew,
      BasicHero,
      ActorDetailSDGs,
      Icon,
      Container,
      ChallengeHero
    },
    data() {
      return {
        subNavSection: '',
        similarLocalChallenges: [],
        comments: [],
        commentsCount: 0,
        togglingPinStatus: false,
        documents: [],
        isLoadingDocuments: false,
        isLoadingUpdates: false,
        updates: [],
        // Store handler references for proper cleanup
        handleChallengeUpdateDelete: null,
        handleFetchDocuments: null,
        handleFileDelete: null,
      }
    },
    computed: {
      canDisplayDocumentsTab(): boolean {
        return this.isOwner || this.isMember || (this.challenge.creator && this.userId === this.challenge.creator.id)
      },
      canDisplayMatchesTab(): boolean {
        return this.isOwner || this.isMember || (this.challenge.creator && this.userId === this.challenge.creator.id)
      },
      challenge() {
        return this.$store.state.challenges.detail.data
      },
      geographicalLabels(): String[] {
        return this.challenge.geographicalLabels.map(geography => {
          return geography.label
        })
      },
      canShowSubscriptions(): boolean {
        return this.$store.getters.hasAccessToEcosystemMemberPackages
      },
      challengeEndDate(): String {
        if (this.challenge.endDate) {
          return moment(this.challenge.endDate).format('LL');
        }

        return ''
      },
      similarAndNextChallenges() {
        const result = this.similarLocalChallenges.concat(this.challenges).filter(challenge => challenge.id !== Number(this.$route.params.id))
        return _uniqBy(result, 'id')
      },
      headerStyle() {
        return {
          padding: '0 0 0.5rem 0',
          justifyContent: 'flex-start',
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 1) !important',
          width: '100%',
          borderBottom: '1px solid var(--primary-community)'
        }
      },
      sdgs(): String[] {
        if (!Array.isArray(this.challenge.sdgs)) return

        if (!this.challenge.sdgs.length) {
          return []
        }

        return this.challenge.sdgs.map((sdg) => {
          return sdg.label
        })
      }
    },
    methods: {
      fromNow,
      getChallengeTimeDifferenceValue,
      getClearbitActorLogo,
      showLoginModal() {
        window.redirect_uri = window.location.href
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
      },
      deleteChallengeUpdate(update) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: 'challenge-update',
          resource: update,
          modalContextType: 'challenge-update',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      addUpdate(content) {
        addChallengeUpdate(this.challengeId, { content: content, challenge_id: this.challengeId })
          .then(() => {
            this.fetchUpdates()
          })
      },
      goToActorDetail() {
        if (this.challenge.actor) {
          window.open(`/actors/${this.challenge.actor.elastic_id}`, '_blank').focus()
        }
      },
      getFileType(document) {
        if (document.meta.find((doc) => doc.key === 'file_type') && document.meta.find((doc) => doc.key === 'file_type').value && document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1]) {

          if (document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1].includes('sheet')) {
            return 'Spreadsheet'
          }

          return document.meta.find((doc) => doc.key === 'file_type').value.split('/')[1]
        }

        if (document.meta.find((doc) => doc.key === 'file_type') && document.meta.find((doc) => doc.key === 'file_type').value) {
          return document.meta.find((doc) => doc.key === 'file_type').value
        }

        return ''
      },
      openResource(document) {
        if (document.meta && document.meta.length > 0 && document.meta[0].value && document.meta[0].value === 'link') {
          if (document.meta.find((data) => data.key === 'url').value != null) {
            window.open(document.meta.find((data) => data.key === 'url').value, '_blank')
            return
          }
        }

        window.open(`${window.location.origin}/api/files/${document.sql_media_id}`, '_blank')
      },
      showDeleteResourceConfirmationModal(resource) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          body: 'Do you want to delete this resource?',
          resource: resource,
          modalContextType: 'file'
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      showEditResourcePanel(document) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          'component': 'edit-resource-panel',
          metaData: { resource: document },
        })
      },
      openCreateResourcePanel() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'create-resource-panel',
          metaData: 'resource',
        })
      },
      togglePinStatus() {
        this.togglingPinStatus = true
        updatePinStatus(this.challenge.id)
          .then((response) => {
            this.challenge.isFollowing = response.isFollowing
          })
          .finally(() => {
            this.togglingPinStatus = false
          })
      },
      fetchSimilarChallenges() {
        fetchSimilarChallenges(this.$route.params.id).then(result => {
          this.similarLocalChallenges = result
        })
      },
      fetchComments() {
        // this fixes problems when rapidly clicking the like button multiple times
        return getComments(Number(this.$route.params.id))
          .then(data => {
            this.comments = data.comments
            this.commentsCount = data.count
          })
          .catch(err => {
            // log the error to sentry
          })
      },
      addComment(comment) {
        createComment(this.challenge.id, comment)
          .then(() => {
            this.fetchComments()
          })
          .catch(errors => {
            this.errors = errors
          })
      },
      async fetchDocuments() {
        this.isLoadingDocuments = true
        let formattedFilter = []
        if (this.challengeId) {
          formattedFilter['linked_challenge_id'] = this.challengeId
        }

        try {
          await fetchKnowledgeBaseData(formattedFilter)
            .then((response) => {
              if (response && response.data && response.data.results) {
                this.documents = response.data.results
              }
            })
            .finally(() => {
              this.isLoadingDocuments = false
            })
        } catch (e) {
          console.log(e)
          this.isLoadingDocuments = false
        }
      },
      deleteResource(resource) {
        if (!resource.sql_media_id) {
          return
        }

        this.deleteEcosystemFile(resource.sql_media_id)
      },
      deleteEcosystemFile(fileId) {
        if (!fileId) {
          return
        }

        deleteEcosystemFile(fileId)
          .then(response => {
            this.resourceToDelete = {}
            // Give the ElasticSearch a small amount of time to update the document, before emitting the event
            setTimeout(() => {
              this.fetchDocuments()
            }, 500)
          })
          .catch(errors => {
            console.log(errors)
          })
      },
      fetchUpdates() {
        if (!this.challengeId) {
          return
        }

        this.isLoadingUpdates = true

        fetchChallengeUpdates(this.challengeId)
          .then((response) => {
            this.updates = response
          })
          .finally(() => {
            this.isLoadingUpdates = false
          })
      },
    },
    async beforeRouteUpdate(to, from, next) {
      if (to.name === 'challenges-detail') {
        this.$route.params.id = to.params.id
        this.fetchChallengeDetail()
      }
      next()
    },
    async mounted() {
      await this.fetchChallengeDetail(this.$route.params.id)
      await this.fetchComments()
      await this.fetchDocuments()
      this.fetchUpdates()
      this.fetchSimilarChallenges()

      // Create and store handler references for proper cleanup
      this.handleChallengeUpdateDelete = (context) => {
        removeChallengeUpdate(context.resource.challengeId, context.resource.id)
          .then(() => {
            this.fetchUpdates()
          })
      }

      this.handleFetchDocuments = (challengeId) => {
        if (Number(challengeId) === Number(this.challengeId)) {
          setTimeout(() => {
            this.fetchDocuments()
          }, 500)
        }
      }

      this.handleFileDelete = (modalContext) => {
        this.deleteResource(modalContext.resource)
      }

      // Register event listeners
      this.$bus.on('challengeUpdateDeleteConfirmation', this.handleChallengeUpdateDelete)
      this.$bus.on('fetch-challenge-documents', this.handleFetchDocuments)
      this.$bus.on('fileDeleteConfirmation', this.handleFileDelete)

      if (this.$route.hash) {
        if (this.$route.hash === '#qanda') {
          this.subNavSection = 'q&a'
          return
        }

        if (this.$route.hash === '#overview') {
          this.subNavSection = ''
          return
        }

        this.subNavSection = this.$route.hash.replace('#', '')
      }
    },
    beforeUnmount() {
      // Clean up only this component's listeners
      if (this.handleFetchDocuments) {
        this.$bus.off('fetch-challenge-documents', this.handleFetchDocuments)
      }
      if (this.handleFileDelete) {
        this.$bus.off('fileDeleteConfirmation', this.handleFileDelete)
      }
      if (this.handleChallengeUpdateDelete) {
        this.$bus.off('challengeUpdateDeleteConfirmation', this.handleChallengeUpdateDelete)
      }
    },
    watch: {
      async challengeId() {
        if (!this.$route.params.id || Number.isNaN(this.$route.params.id)) {
          return
        }

        await this.fetchChallengeDetail(this.$route.params.id)
        this.fetchSimilarChallenges()
        await this.fetchComments()
        await this.fetchDocuments()
        this.fetchUpdates()

        this.subNavSection = ''

        if (this.$refs.scrollable) {
          this.$refs.scrollable.scrollTop = 0
        }
      }
    }
  })
</script>

<style lang="scss" scoped>
  @import "../../../scss/variables";

  .challenge-detail {
    overflow: scroll;
    height: 100vh;
  }

  .challenge-content {
    padding: 1rem;
  }

  .challenge-content-container {
    display: flex;
  }

  .challenge-info {
    margin-top: 1rem;
    margin-bottom: 1rem;
    border: 1px solid lightgrey;
    background: white;
    border-radius: 0.5em;
    box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);

    width: 250px;
    height: auto;

    .challenge-info-title {
      text-align: center;
      font-weight: bold;

      border-bottom: 1px solid lightgrey;
      width: 80%;
      margin: 2rem auto 1rem auto;
      padding-bottom: 1rem;
    }

    .challenge-contact {
      padding: 1rem;
      display: flex;
      flex-direction: row;

      .challenge-contact-department {
        color: var(--primary-dark);
        font-size: 11px;
      }
    }

    .challenge-contact-button {
      padding: 0rem 1rem 1rem 1rem;
      width: 100%;
    }
  }

  .challenge-organisation-logo {
    cursor: pointer;
    border: 1px solid lightgrey;
    background: white;
    border-radius: 0.5em;
    box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);

    border: 1px solid transparent;

    &:hover {
      border: 1px solid var(--primary-community);
    }

    display: flex;
    flex-direction: column;

    width: 250px;
    min-height: 250px;
    justify-content: center;
    align-items: center;
    margin-top: -5.5rem;
    z-index: 50;
    position: relative;

    .challenge-organisation-description {
      padding: 2rem;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-bottom: 2rem;
    }

    .challenge-organisation-image {
      width: 100px;
      height: 100px;
      border-radius: 0.5rem;
      margin-top: 2rem;
    }
  }

  .challenge-bottom-container {
    .challenge-goals, .challenge-enddate {
      display: flex;
      flex-direction: row;
      margin: auto;
      text-align: center;
      width: 80%;
    }


    .challenge-enddate {
      margin-top: 2rem;
      margin-bottom: 1rem;
    }

    .challenge-actions {
      display: flex;
      flex-direction: column;
      width: 80%;
      margin: 1rem auto;
    }

    .challenge-goals {
      margin-bottom: 1rem;
      text-align: left;
    }
  }

  .challenge-content-right-side {
    padding: 0 2rem 2rem 2rem;
    width: 100%;
  }

  .challenge-content-left-side {
    position: relative;

    .challenge-logo-sdg-container {
      position: relative;

      .sdg-no-actors {
        top: -3.5rem;
        left: 1rem;
      }
    }

    .sdg-container {
      position: absolute;
      top: 2rem;
      left: 280px;
      z-index: 51;
      min-width: 250px;
      justify-content: flex-start;
    }
  }

  .challenge-detail__more-challenges {
    padding-bottom: 2rem;
    margin-bottom: 2rem;
  }

  .challenge-content-right-side {
    .basic-hero.basic-hero__header {
      display: none !important;
    }
  }

  .challenge-content-section {
    margin-bottom: 2rem;

    h2 {
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 15px;
    }
  }

  .documents-container {
    display: flex;
    flex-direction: column;

    .documents-actions {
      display: flex;
      width: 100%;
      justify-content: flex-end;
    }

    .documents-list {
      display: flex;
      flex-direction: column;
    }

    .document-card {
      cursor: pointer;
      border: 1px solid var(--primary-community);
      border-radius: 0.5rem;
      width: 100%;
      padding: 0.5rem;
      margin-top: 0.5rem;

      &:hover {
        box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);
      }

      display: flex;
      flex-direction: row;

      justify-content: space-between;

      .document-content {
        display: flex;
        flex-direction: column;
      }

      .document-actions {
        display: flex;
        flex-direction: row;
      }
    }
  }

  .activity-container {
    display: flex;
    flex-direction: column;

    .activity-list {
      display: flex;
      flex-direction: column;
    }

    .activity-card {
      border: 1px solid var(--primary-community);
      border-radius: 0.5rem;
      width: 100%;
      padding: 0.5rem;
      margin-top: 0.5rem;

      &:hover {
      }

      display: flex;
      flex-direction: row;

      justify-content: space-between;

      .activity-content {
        display: flex;
        flex-direction: column;
        width: 100%;

        .activity-user {
          display: flex;
          width: 100%;
          flex-direction: row;
          justify-content: space-between;
        }
      }
    }
  }

  .profile-tabs-container {
    background-color: var(--primary-community-extra-lightest);

    .with-profile-tabs--tabs {
      display: flex;

      .profile-tab {
        color: var(--primary-community);
        width: 100%;
        height: 50px;
        line-height: 50px;
        cursor: pointer;
        padding: 0 20px;
        text-decoration: none;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;


        &:hover, &.selected {
          background: var(--primary-community-lightest);
          font-weight: 500;
        }
      }

      &.has-3-tabs {
        .profile-tab {
          @media (min-width: $screen-lg) {
            width: 33%;
          }
        }

        &.align-left {
          .profile-tab {
            @media (min-width: $screen-lg) {
              width: 25%;
            }
          }
        }
      }
    }
  }

  .search-actor-list-result {
    cursor: pointer;
    padding: 0.5rem;
    border: 1px solid var(--primary-community-lightest);
    background-color: var(--primary-community-lightest);
    border-radius: 0.25rem;

    &:hover {
      box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);
    }
  }

  .challenge-actor-suggestions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;

    .challenge-actor-suggestion {
      width: 250px;
    }
  }
</style>
<script setup lang="ts">
</script>
